// The plan's text boxes: picked, their grips (resize, the leader's knob), the
// box being drawn, edited in place in the overlay textarea, and sized to their
// text. docs/claude/tools.md.
import { type EditorLook } from '../inlineText';
import {
  type TextComment,
  type Vec2,
} from '../../shared/types';
import {
  MULTI_KINDS,
  selectedCount,
  selectOne,
} from '../selection';
import {
  invalidate,
  newId,
  pushHistory,
  renderProperties,
  state,
  vp,
} from './core';
import {
  ANNOTATION_COLOR,
  COMMENT_DEFAULT_FILL,
  COMMENT_DEFAULT_OPACITY,
  COMMENT_FONT_MM,
  COMMENT_FONT_PX,
  COMMENT_LINE_PX,
  COMMENT_PAD_PX,
  Handle,
  textCommentHandles,
  textRotationKnob,
  wrapText,
} from '../render';
import {
  textBoxLocal,
  textBoxToWorld,
} from '../textBox';
import {
  ALIGN_TOL_PX,
  alignResizeSnap,
  ctx,
  deselectAll,
  endOneShot,
  HANDLE_PX,
  otherTextRects,
  textEditorUi,
} from '../main';

/** This module's state that main.ts reads and writes too. */
export const textBoxes = {
  // Text-comment tool: the points placed so far.
  textDraft: {} as { a?: Vec2; b?: Vec2; leader?: Vec2 },
  // True while an existing text comment is being re-edited inline, so the typed
  // change is recorded as one undo step on close. A freshly-created box (isNew)
  // rides on its creation snapshot instead, keeping creation a single undo.
};

// ---- Text-box comments ----

/** The comment currently being edited inline, or null. */
export function editingComment(): TextComment | null {
  return (state.drawing.texts || []).find((t) => t.id === state.editingTextId) || null;
}

/** The resize handle of the selected text comment under a screen point, or null. */
export function textHandleAtScreen(sp: Vec2): Handle | null {
  const t = (state.drawing.texts || []).find((x) => x.id === state.selectedTextId);
  if (!t) return null;
  for (const h of textCommentHandles(t)) {
    const s = vp.worldToScreen(h.world);
    if (Math.abs(s.x - sp.x) <= HANDLE_PX && Math.abs(s.y - sp.y) <= HANDLE_PX) return h;
  }
  return null;
}

/** True when the screen point is on the selected text box's rotate knob. */
export function textKnobAt(sp: Vec2): boolean {
  if (state.tool !== 'select' || selectedCount(state, MULTI_KINDS) !== 1) return false;
  const t = (state.drawing.texts || []).find((x) => x.id === state.selectedTextId);
  if (!t || state.editingTextId === t.id) return false;
  const k = textRotationKnob(vp, t).knob;
  return Math.hypot(k.x - sp.x, k.y - sp.y) <= HANDLE_PX;
}

/** True when the screen point is on the selected text box's leader-arrow tip
 *  handle (drawn only when the box has a leader), so the tip can be dragged. */
export function textLeaderHandleAt(sp: Vec2): boolean {
  const t = (state.drawing.texts || []).find((x) => x.id === state.selectedTextId);
  if (!t?.leader) return false;
  const s = vp.worldToScreen(t.leader);
  return Math.abs(s.x - sp.x) <= HANDLE_PX && Math.abs(s.y - sp.y) <= HANDLE_PX;
}

/** Resize the selected text box by dragging a handle. This scales the *font*
 *  (and the box with it, uniformly) rather than reshaping the box, anchored at
 *  the corner/edge opposite the dragged handle. */
/**
 * Resize a text box by dragging one of its handles. This changes the BOX ONLY —
 * the font stays exactly as it was, so the text simply re-wraps to the new width
 * and overflows the box when it no longer fits. Use the Text height field, or
 * Enlarge / Reduce, to change the type size (world mm).
 */
export function resizeText(t: TextComment, h: Handle, mouse: Vec2, noAlign = false): void {
  const MIN = 4 / vp.scale; // a few pixels, so a box can never collapse to nothing
  if (t.rotation) {
    // A turned box resizes in its own frame (x right, y DOWN from the anchor),
    // then re-anchors at its new top-left corner; no edge alignment when turned.
    const m = textBoxLocal(t, mouse);
    let l = 0, r = t.w, top = 0, bot = t.h;
    if (h.sx === -1) l = Math.min(m.x, r - MIN); else if (h.sx === 1) r = Math.max(m.x, l + MIN);
    if (h.sy === 1) top = Math.min(m.y, bot - MIN); else if (h.sy === -1) bot = Math.max(m.y, top + MIN);
    const a = textBoxToWorld(t, l, top);
    t.x = a.x; t.y = a.y; t.w = r - l; t.h = bot - top;
    state.alignGuides = [];
    return;
  }
  let left = t.x, right = t.x + t.w, top = t.y, bottom = t.y - t.h; // world Y-up
  if (h.sx === -1) left = Math.min(mouse.x, right - MIN);
  else if (h.sx === 1) right = Math.max(mouse.x, left + MIN);
  // `textCommentHandles` places sy=1 at the top (world.y = t.y) and sy=-1 at the
  // bottom (t.y - t.h), so the grabbed edge must match: sy=1 moves the top edge,
  // sy=-1 the bottom. (Reversing these drove the top down when the bottom handle
  // was dragged, collapsing the box flat.)
  if (h.sy === 1) top = Math.max(mouse.y, bottom + MIN);
  else if (h.sy === -1) bottom = Math.min(mouse.y, top - MIN);
  // Align the grabbed edges to other text boxes' projections (Alt suspends).
  if (noAlign) { state.alignGuides = []; }
  else {
    const snapped = alignResizeSnap(
      { x0: left, x1: right, y0: bottom, y1: top }, otherTextRects(t.id),
      { x0: h.sx === -1, x1: h.sx === 1, y1: h.sy === 1, y0: h.sy === -1 }, ALIGN_TOL_PX / vp.scale);
    left = snapped.x0; right = snapped.x1; bottom = snapped.y0; top = snapped.y1;
  }
  t.x = left; t.y = top; t.w = right - left; t.h = top - bottom;
}

/** Build the live preview of the box being placed with the text tool. */
export function pendingTextBoxPreview(): { x: number; y: number; w: number; h: number; leader?: Vec2 } | null {
  if (state.tool !== 'text' || !textBoxes.textDraft.a) return null;
  const b = textBoxes.textDraft.b ?? state.hoverWorld;
  if (!b) return null;
  const x = Math.min(textBoxes.textDraft.a.x, b.x);
  const yTop = Math.max(textBoxes.textDraft.a.y, b.y);
  const w = Math.abs(textBoxes.textDraft.a.x - b.x);
  const h = Math.abs(textBoxes.textDraft.a.y - b.y);
  const leader = textBoxes.textDraft.b ? textBoxes.textDraft.leader ?? state.hoverWorld ?? undefined : undefined;
  return { x, y: yTop, w, h, leader };
}

/** Commit the in-progress text box (corners + optional leader) and start typing. */
export function finishTextComment(): void {
  if (!textBoxes.textDraft.a || !textBoxes.textDraft.b) {
    textBoxes.textDraft = {};
    return;
  }
  pushHistory();
  const x = Math.min(textBoxes.textDraft.a.x, textBoxes.textDraft.b.x);
  const yTop = Math.max(textBoxes.textDraft.a.y, textBoxes.textDraft.b.y);
  // A usable minimum box tied to the default type size (world mm), so a fresh box
  // is born big enough to show at least one line of text at that size.
  const padMm = COMMENT_FONT_MM * (COMMENT_PAD_PX / COMMENT_FONT_PX);
  const lineMm = COMMENT_FONT_MM * (COMMENT_LINE_PX / COMMENT_FONT_PX);
  const minW = padMm * 2 + COMMENT_FONT_MM * 8; // room for a short line
  const minH = lineMm + padMm * 2;              // one line
  const w = Math.max(minW, Math.abs(textBoxes.textDraft.a.x - textBoxes.textDraft.b.x));
  const h = Math.max(minH, Math.abs(textBoxes.textDraft.a.y - textBoxes.textDraft.b.y));
  const c: TextComment = {
    id: newId('text'),
    x,
    y: yTop,
    w,
    h,
    leader: textBoxes.textDraft.leader,
    text: '',
    fill: COMMENT_DEFAULT_FILL,
    fillOpacity: COMMENT_DEFAULT_OPACITY,
  };
  if (!state.drawing.texts) state.drawing.texts = [];
  state.drawing.texts.push(c);
  textBoxes.textDraft = {};
  endOneShot();
  deselectAll();
  selectOne(state, 'text', c.id); // the new object alone is selected
  openTextEditor(c, true); // creation already pushed history; editing rides on it
  renderProperties();
  invalidate();
}

/** Open the inline editor over a comment's box. `isNew` skips the undo gesture
 *  for a box that was just created (its content is part of the creation step).
 *  Enter commits, Shift+Enter is a new line; Escape keeps what was typed. */
export function openTextEditor(c: TextComment, isNew = false): void {
  textEditorUi.close(true);
  state.editingTextId = c.id;
  state.selectedTextId = c.id;
  let look: EditorLook = commentLook(c);
  textEditorUi.open({
    text: c.text,
    look: () => { const cc = editingComment(); if (cc) look = commentLook(cc); return look; },
    keys: { enter: 'shift-newline', esc: 'commits' },
    gesture: !isNew,
    caret: 'end',
    // The box keeps the size it was given: typing past it overflows rather than
    // growing the box out from under an explicitly set Height.
    input: (t) => { const cc = editingComment(); if (cc) { cc.text = t; invalidate(); } },
    commit: (t) => { const cc = editingComment(); if (cc) cc.text = t; },
    // The box stays even when empty (it was placed on purpose): Delete removes it.
    closed: () => { state.editingTextId = null; renderProperties(); invalidate(); },
  });
  invalidate();
}

/** Where a text box's inline editor sits and how it looks: over the box, turned
 *  with it about its top-left, in its text colour and metrics, so wrapping and
 *  the box's fit agree while typing. */
function commentLook(c: TextComment): EditorLook {
  const fontPx = (c.heightMm ?? COMMENT_FONT_MM) * vp.scale;
  const tl = vp.worldToScreen({ x: c.x, y: c.y });
  return {
    left: `${tl.x}px`, top: `${tl.y}px`,
    width: `${Math.max(20, c.w * vp.scale)}px`, height: `${Math.max(20, c.h * vp.scale)}px`,
    transformOrigin: '0 0', transform: c.rotation ? `rotate(${-c.rotation}deg)` : '',
    color: c.textColor ?? ANNOTATION_COLOR,
    fontSize: `${fontPx}px`,
    lineHeight: `${fontPx * (COMMENT_LINE_PX / COMMENT_FONT_PX)}px`,
    padding: `${fontPx * (COMMENT_PAD_PX / COMMENT_FONT_PX)}px`,
  };
}

export const COMMENT_MIN_MM = 5;    // sane floor / ceiling for a text box's type height (world mm)
export const COMMENT_MAX_MM = 2000;

/** The wrapped-content size (world mm) of a text box at its current text height and
 *  width — used to grow a box just enough to hold its text. Measures in mm-space by
 *  setting the font size to the mm value (1 canvas unit = 1 mm). */
function textBoxContentSize(t: TextComment): { w: number; h: number } {
  const mm = t.heightMm ?? COMMENT_FONT_MM;
  const padMm = mm * (COMMENT_PAD_PX / COMMENT_FONT_PX);
  const lineMm = mm * (COMMENT_LINE_PX / COMMENT_FONT_PX);
  const prev = ctx.font;
  ctx.font = `${mm}px sans-serif`;
  const innerW = Math.max(1, t.w - padMm * 2);
  const lines = wrapText(ctx, t.text || '', innerW);
  let maxW = 0;
  for (const l of lines) maxW = Math.max(maxW, ctx.measureText(l).width);
  ctx.font = prev;
  return { w: maxW + padMm * 2, h: Math.max(1, lines.length) * lineMm + padMm * 2 };
}

/** Grow a box (downward / rightward, top-left fixed) just enough to contain its
 *  text. Never shrinks — resizing the box by hand stays the way to make it smaller. */
export function growTextBoxToFit(t: TextComment): void {
  const need = textBoxContentSize(t);
  if (need.h > t.h) t.h = need.h;
  if (need.w > t.w) t.w = need.w;
}

/** Set a text box's type height (world mm). The box is NOT scaled — it only grows
 *  when the new size no longer fits the text (decided by `growTextBoxToFit`). */
export function setTextCommentHeight(t: TextComment, mm: number): void {
  const next = Math.max(COMMENT_MIN_MM, Math.min(COMMENT_MAX_MM, mm));
  if (Math.abs(next - (t.heightMm ?? COMMENT_FONT_MM)) < 1e-6) return;
  pushHistory();
  t.heightMm = next;
  growTextBoxToFit(t);
  renderProperties();
  invalidate();
}

/** Enlarge (×2) / Reduce (÷2) a text box's type size. Like the Text height field it
 *  changes only the type size; the box grows only if the text then overflows. */
export function scaleTextComment(t: TextComment, factor: number): void {
  setTextCommentHeight(t, (t.heightMm ?? COMMENT_FONT_MM) * factor);
}
